import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { CheckCircleOutline, DeleteOutline } from "@mui/icons-material";
import { Box, useTheme, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deleteTask, fetchTasks } from "../redux/actions/taskActions";
import { largeBold, thinBold, typoBase, typoThin } from "../styles/typography";
import { formatDate } from "../utils/dateUtils";

const formatDateTask = (dateString) => formatDate(dateString);

const calculateOverdueDays = (dueDate) => {
  const currentDate = new Date();
  const due = new Date(dueDate);
  const diffTime = Math.abs(currentDate - due);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const TasksOverview = ({ filteredTasks }) => {
  const { t } = useTranslation(['common', 'pages']);
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("All");
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const completedTasksCount = filteredTasks.filter(
    (task) => task.status === t('common:completed')
  ).length;
  const overdueTasksCount = filteredTasks.filter(
    (task) => new Date(task.dueDate) < new Date() && task.status !== t('common:completed')
  ).length;
  const upcomingTasksCount = filteredTasks.filter(
    (task) =>
      new Date(task.dueDate) >= new Date() && task.status !== t('common:completed')
  ).length;

  const filteredAndSortedTasks = filteredTasks
    .filter((task) => {
      if (filter === "All") return true;
      if (filter === "Completed") return task.status === t('common:completed');
      if (filter === "Overdue")
        return (
          new Date(task.dueDate) < new Date() && task.status !== t('common:completed')
        );
      if (filter === "Upcoming")
        return (
          new Date(task.dueDate) >= new Date() && task.status !== t('common:completed')
        );
      return true;
    })
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  const handleDeleteClick = (taskId, e) => {
    e.stopPropagation();
    setTaskToDelete(taskId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (taskToDelete) {
      try {
        // Delete the task
        await dispatch(deleteTask(taskToDelete));
        // Wait a moment for the server to process, then refresh
        setTimeout(() => {
          dispatch(fetchTasks());
        }, 300);
      } catch (error) {
        console.error('Error deleting task:', error);
        alert(error?.message || 'Failed to delete task. Please try again.');
      }
    }
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-evenly",
          alignItems: "center",
          marginTop: "-10px",
        }}
      >
        <div
          style={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setFilter("All")}
        >
          <h4
            style={{
              color: "rgb(53, 53, 53)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "15px",
              fontWeight: 600,
              height: "19.5px",
              lineHeight: "19.5px",
            }}
          >
            {filteredTasks.length}
          </h4>
          <p
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "11.5px",
              height: "15.75px",
              lineHeight: "15.75px",
              marginTop: "-15px", // Keeping the existing marginTop style
            }}
          >
            {t('common:all')}
          </p>
        </div>
        <div
          style={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setFilter("Completed")}
        >
          <h4
            style={{
              color: "rgb(53, 53, 53)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "15px",
              fontWeight: 600,
              height: "19.5px",
              lineHeight: "19.5px",
            }}
          >
            {completedTasksCount}
          </h4>
          <p
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "11.5px",
              height: "15.75px",
              lineHeight: "15.75px",
              marginTop: "-15px", // Keeping the existing marginTop style
            }}
          >
            {t('common:completed')}
          </p>
        </div>
        <div
          style={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setFilter("Overdue")}
        >
          <h4
            style={{
              color: "rgb(53, 53, 53)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "15px",
              fontWeight: 600,
              height: "19.5px",
              lineHeight: "19.5px",
            }}
          >
            {overdueTasksCount}
          </h4>
          <p
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "11.5px",
              height: "15.75px",
              lineHeight: "15.75px",
              marginTop: "-15px", // Keeping the existing marginTop style
            }}
          >
            {t('common:overdue')}
          </p>
        </div>
        <div
          style={{ textAlign: "center", cursor: "pointer" }}
          onClick={() => setFilter("Upcoming")}
        >
          <h4
            style={{
              color: "rgb(53, 53, 53)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "15px",
              fontWeight: 600,
              height: "19.5px",
              lineHeight: "19.5px",
            }}
          >
            {upcomingTasksCount}
          </h4>
          <p
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "11.5px",
              height: "15.75px",
              lineHeight: "15.75px",
              marginTop: "-15px", // Keeping the existing marginTop style
            }}
          >
            {t('pages:tasks.to_do')}
          </p>
        </div>
      </div>
      <Box
        sx={{
          marginTop: 1,
          overflowY: "auto",
          maxHeight: "380px",
          "&::-webkit-scrollbar": {
            width: "5px", // Set the width of the scrollbar
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "lightgrey", // Set the color of the scrollbar thumb
            borderRadius: "0", // Set the border radius of the scrollbar thumb
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#888", // Set the color of the scrollbar thumb on hover
          },
        }}
      >
        {filteredAndSortedTasks?.map((task) => (
          <Box
            key={task._id}
            onMouseEnter={() => setHoveredTaskId(task._id)}
            onMouseLeave={() => setHoveredTaskId(null)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid lightgrey",
              padding: "5px",
              paddingTop: "5px",
              "&:hover": {
                backgroundColor: "#ecf5fb",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleOutline
                    sx={{
                      color:
                      task.status === "Completed"
                        ? "rgb(133, 188, 32)"
                        : "lightgrey",
                      fontSize: "18px",
                      marginRight: "10px",
                    }}
                  />
                  <Box
                    sx={{
                      width: "10px",
                      height: "10px",
                      backgroundColor: task.colour,
                      marginRight: "5px",
                    }}
                  />
                  <Box
                    sx={{
                      color: "rgb(0, 91, 146)",
                      cursor: "pointer",
                      display: "block",
                      fontFamily:
                        '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                      fontSize: "11.5px",
                      fontWeight: 400,
                      height: "13.5px",
                      lineHeight: "13.5px",

                      paddingBottom: "2px",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                      maxWidth: "25ch",
                      marginTop: "4px",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => navigate(`/task/${task._id}`)}
                  >
                    {task.subject}
                  </Box>
                </Box>
                <Box
                  sx={{
                    color: "rgb(0, 91, 146)",
                    cursor: "pointer",
                    display: "block",
                    fontFamily:
                      '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "11.5px",
                    fontWeight: 400,
                    height: "13.5px",
                    lineHeight: "13.5px",

                    paddingBottom: "2px",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                    maxWidth: "25ch",
                    marginLeft: "24px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => navigate(`/files/${task?.case?._id}/overview`)}
                >
                  {task.case?.fileNo} {task.case?.name}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  {hoveredTaskId === task._id && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleDeleteClick(task._id, e)}
                      sx={{
                        padding: "2px",
                        color: "rgb(229, 32, 46)",
                        "&:hover": {
                          backgroundColor: "rgba(229, 32, 46, 0.1)",
                        },
                      }}
                    >
                      <DeleteOutline sx={{ fontSize: "16px" }} />
                    </IconButton>
                  )}
                  <Box
                    sx={{
                      color: "rgb(53, 53, 53)",
                      display: "block",
                      fontFamily:
                        '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                      fontSize: "11.5px",
                      fontWeight: 400,
                      height: "15.75px",
                      lineHeight: "15.75px",
                    }}
                  >
                    {formatDateTask(task.dueDate)}
                  </Box>
                </Box>
                <Box
                  sx={{
                    color: "rgb(229, 32, 46)",
                    display: "block",
                    fontFamily:
                      '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                    fontSize: "10.5px",
                    fontWeight: 400,
                    height: "15.75px",
                    lineHeight: "15.75px",
                  }}
                >
                  {new Date(task.dueDate) < new Date() &&
                  task.status !== t('common:completed')
                    ? t('pages:tasks.days_late', { days: calculateOverdueDays(task.dueDate) })
                    : null}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('common:confirm_delete') || 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('common:confirm_delete_task') || 'Are you sure you want to delete this task? This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('common:cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            {t('common:delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TasksOverview;
